import { useState, Fragment } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Plus, Edit, Trash2, Loader2, ChevronRight, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { ProductFormModal } from "./product-form-modal";
import { PricingTiersPanel } from "./pricing-tiers-panel";
import type { LenderProduct } from "@shared/schema";
import { format } from "date-fns";

// ---------------------------------------------------------------------------
// Eligibility helpers (AC004, AC005)
// ---------------------------------------------------------------------------

function hasEligibilityCriteria(product: LenderProduct): boolean {
  return (
    product.minDscr != null ||
    product.minIcr != null ||
    product.maxDebtToEbitda != null ||
    (Array.isArray(product.acceptableSecurityTypes) && product.acceptableSecurityTypes.length > 0) ||
    (Array.isArray(product.acceptableEntityTypes) && product.acceptableEntityTypes.length > 0) ||
    (Array.isArray(product.geographicRestrictions) && product.geographicRestrictions.length > 0) ||
    product.requiresExistingCustomer === true
  );
}

function buildEligibilityTooltip(product: LenderProduct): string {
  const lines: string[] = [];
  if (product.minDscr != null) lines.push(`Min DSCR: ${product.minDscr}x`);
  if (product.minIcr != null) lines.push(`Min ICR: ${product.minIcr}x`);
  if (product.maxDebtToEbitda != null) lines.push(`Max Debt/EBITDA: ${product.maxDebtToEbitda}x`);
  if (Array.isArray(product.acceptableSecurityTypes) && product.acceptableSecurityTypes.length > 0) {
    lines.push(`Security Types: ${product.acceptableSecurityTypes.join(", ")}`);
  }
  if (Array.isArray(product.acceptableEntityTypes) && product.acceptableEntityTypes.length > 0) {
    lines.push(`Entity Types: ${product.acceptableEntityTypes.join(", ")}`);
  }
  if (Array.isArray(product.geographicRestrictions) && product.geographicRestrictions.length > 0) {
    lines.push(`Regions: ${product.geographicRestrictions.join(", ")}`);
  }
  if (product.requiresExistingCustomer) lines.push("Existing customers only");
  return lines.join("\n");
}

interface ProductsPanelProps {
  lenderId: string;
}

export function ProductsPanel({ lenderId }: ProductsPanelProps) {
  const [showProductForm, setShowProductForm] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<LenderProduct | undefined>();
  const [deleteProductId, setDeleteProductId] = useState<string | null>(null);
  const [expandedProductId, setExpandedProductId] = useState<string | null>(null);

  const { toast } = useToast();

  const { data: products = [], isLoading } = useQuery<LenderProduct[]>({
    queryKey: ["/api/crm/lenders", lenderId, "products"],
  });

  // Fetch pricing tier counts for all products
  const { data: tierCounts = {} } = useQuery<Record<string, number>>({
    queryKey: ["/api/crm/lenders", lenderId, "products", "tier-counts", products],
    queryFn: async ({ queryKey }) => {
      const prods = queryKey[4] as LenderProduct[];
      if (!prods.length) return {};

      const counts: Record<string, number> = {};
      await Promise.all(
        prods.map(async (product) => {
          const response = await apiRequest(
            "GET",
            `/api/crm/lender-products/${product.id}/pricing-tiers`
          );
          const tiers = await response.json();
          counts[product.id] = tiers?.length || 0;
        })
      );
      return counts;
    },
    enabled: !!products.length,
  });

  // Delete product mutation
  const deleteProductMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/crm/lender-products/${id}`);
    },
    onSuccess: () => {
      // Use predicate to match ALL lender-related queries (fixes ISSUE-072)
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey.some((key) =>
            typeof key === "string" && key.includes("/api/crm/lenders")
          ),
      });
      toast({
        title: "Product deleted",
        description: "The product has been deleted successfully.",
      });
      setDeleteProductId(null);
      setExpandedProductId(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete product",
        variant: "destructive",
      });
    },
  });

  const handleEdit = (product: LenderProduct) => {
    setSelectedProduct(product);
    setShowProductForm(true);
  };

  const handleCloseModal = () => {
    setShowProductForm(false);
    setSelectedProduct(undefined);
  };

  const toggleProductExpand = (productId: string) => {
    setExpandedProductId(expandedProductId === productId ? null : productId);
  };

  const formatDate = (dateValue: Date | string | null | undefined) => {
    if (!dateValue) return "-";
    try {
      const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
      return format(date, "dd MMM yyyy");
    } catch {
      return "-";
    }
  };

  if (isLoading) {
    return <div className="p-4">Loading products...</div>;
  }

  return (
    <TooltipProvider>
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Products</h3>
        <Button
          size="sm"
          onClick={() => setShowProductForm(true)}
          data-testid="button-add-product"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Product
        </Button>
      </div>

      {products.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">
            No products found. Click "Add Product" to create one.
          </CardContent>
        </Card>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow className="h-9 bg-muted/50">
                <TableHead className="text-xs font-medium px-3 py-1.5 w-[40px]"></TableHead>
                <TableHead className="text-xs font-medium px-3 py-1.5">Product Name</TableHead>
                <TableHead className="text-xs font-medium px-3 py-1.5">Code</TableHead>
                <TableHead className="text-xs font-medium px-3 py-1.5">Category</TableHead>
                <TableHead className="text-xs font-medium px-3 py-1.5">Loan Type</TableHead>
                <TableHead className="text-xs font-medium px-3 py-1.5">Rate Type</TableHead>
                <TableHead className="text-xs font-medium px-3 py-1.5 text-right">Interest Rate</TableHead>
                <TableHead className="text-xs font-medium px-3 py-1.5 text-center">Status</TableHead>
                <TableHead className="text-xs font-medium px-3 py-1.5 text-center">Eligibility</TableHead>
                <TableHead className="text-xs font-medium px-3 py-1.5">Created</TableHead>
                <TableHead className="text-xs font-medium px-3 py-1.5">Updated</TableHead>
                <TableHead className="text-xs font-medium px-3 py-1.5 w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((product) => {
                const isExpanded = expandedProductId === product.id;
                return (
                  <Fragment key={product.id}>
                    <TableRow
                      className="h-10 hover:bg-accent/50 transition-colors cursor-pointer"
                      data-testid={`row-product-${product.id}`}
                      onClick={() => toggleProductExpand(product.id)}
                    >
                      <TableCell className="px-3 py-1.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleProductExpand(product.id);
                          }}
                          data-testid={`button-expand-product-${product.id}`}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="px-3 py-1.5">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{product.productName}</span>
                          {tierCounts[product.id] > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {tierCounts[product.id]} {tierCounts[product.id] === 1 ? "Tier" : "Tiers"}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="px-3 py-1.5">
                        <span className="text-sm text-muted-foreground">{product.productCode}</span>
                      </TableCell>
                      <TableCell className="px-3 py-1.5">
                        <span className="text-sm capitalize">
                          {product.productCategory?.replace(/_/g, " ") || "-"}
                        </span>
                      </TableCell>
                      <TableCell className="px-3 py-1.5">
                        <span className="text-sm capitalize">
                          {product.loanType?.replace(/_/g, " ") || "-"}
                        </span>
                      </TableCell>
                      <TableCell className="px-3 py-1.5">
                        <span className="text-sm capitalize">{product.interestRateType || "-"}</span>
                      </TableCell>
                      <TableCell className="px-3 py-1.5 text-right">
                        <span className="text-sm font-medium tabular-nums">
                          {product.currentBaseRate
                            ? `${(parseFloat(product.currentBaseRate) * 100).toFixed(2)}%`
                            : "-"}
                        </span>
                      </TableCell>
                      <TableCell className="px-3 py-1.5 text-center">
                        <Badge variant={product.status === "active" ? "default" : "secondary"}>
                          {product.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-3 py-1.5 text-center">
                        <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                {hasEligibilityCriteria(product) ? (
                                  <Badge
                                    className="cursor-default bg-teal-100 text-teal-800 border-teal-200 hover:bg-teal-100"
                                    data-testid="eligibility-badge"
                                  >
                                    Criteria set
                                  </Badge>
                                ) : (
                                  <Badge
                                    variant="secondary"
                                    className="cursor-default"
                                    data-testid="eligibility-badge"
                                  >
                                    No criteria
                                  </Badge>
                                )}
                              </span>
                            </TooltipTrigger>
                            {hasEligibilityCriteria(product) && (
                              <TooltipContent
                                data-testid="eligibility-tooltip-content"
                                className="whitespace-pre-line max-w-xs"
                              >
                                {buildEligibilityTooltip(product)}
                              </TooltipContent>
                            )}
                        </Tooltip>
                      </TableCell>
                      <TableCell className="px-3 py-1.5">
                        <span className="text-sm text-muted-foreground">
                          {formatDate(product.createdAt)}
                        </span>
                      </TableCell>
                      <TableCell className="px-3 py-1.5">
                        <span className="text-sm text-muted-foreground">
                          {formatDate(product.updatedAt)}
                        </span>
                      </TableCell>
                      <TableCell className="px-3 py-1.5">
                        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => handleEdit(product)}
                            data-testid={`button-edit-product-${product.id}`}
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => setDeleteProductId(product.id)}
                            data-testid={`button-delete-product-${product.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={12} className="bg-muted/30 p-4">
                          <div className="border rounded-lg bg-background p-4">
                            <PricingTiersPanel lenderProductId={product.id} />
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <ProductFormModal
        open={showProductForm}
        onOpenChange={handleCloseModal}
        defaultLenderId={lenderId}
        product={selectedProduct}
      />

      {/* Delete Product Confirmation Dialog */}
      <Dialog open={!!deleteProductId} onOpenChange={(open) => !open && setDeleteProductId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Product</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this product? This will also delete all associated pricing tiers and commissions. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteProductId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteProductId && deleteProductMutation.mutate(deleteProductId)}
              disabled={deleteProductMutation.isPending}
              data-testid="button-confirm-delete-product"
            >
              {deleteProductMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
}
